/**
 * Camera mounting accessories from the manufacturers' own datasheets, for the
 * camera panel's accessory picker. Unlike the brackets that reached the camera
 * catalog through scraping (rows that carry a camera's resolution and field of
 * view), each entry here is a bracket described as a bracket, and it can be
 * attached to a camera of any brand: installers put Ajax cameras on Dahua pole
 * mounts. The pick is stored on the camera as `selectedAccessory` and counted
 * in the bill of materials and the quote.
 */
import { IPRO_MOUNT_ACCESSORIES } from './mountAccessories-ipro.generated';
import { MOUNTS_FROM_CAMERA_ROWS } from './mountAccessories-from-camera-rows.generated';
import { HIKVISION_MOUNT_ACCESSORIES } from './mountAccessories-hikvision.generated';
import { UNIVIEW_MOUNT_ACCESSORIES } from './mountAccessories-uniview.generated';

export type MountKind =
  | 'pole-mount' | 'wall-mount' | 'corner-mount' | 'ceiling-mount' | 'parapet-mount' | 'plane-mount'
  | 'junction-box' | 'power-box' | 'adapter';

export interface MountAccessory {
  manufacturer: string;
  model: string;
  kind: MountKind;
  material?: string;
  colour?: string;
  /** Load the bracket is rated for, kg. */
  loadKg?: number;
  /** Pole diameters the bracket fits, mm (pole mounts). */
  poleDiameterMm?: [number, number];
  /** Bracket only, length × width × height, mm. */
  dimensionsMm?: [number, number, number];
  weightKg?: number;
  /** How far the accessory moves the camera out from what it is fixed to,
   *  mm: a junction box's or adapter's depth, read from the maker's dimension
   *  drawing (Dahua prints it as H; checked on the PFA126, PFA121, PFA120 and
   *  PFA6330X drawings). Pole brackets have none: their plate sits where the
   *  mount chain already puts the drawn bracket. */
  depthMm?: number;
  /** Pole brackets: depth of the plate off the pole, mm, from the drawing's top
   *  view (45 for the V cradle of the PFA150 family, 20 for the flat PFA152-E). */
  bracketDepthMm?: number;
  /** Arms (wall, parapet, plane): wall face or post to the camera's fixing
   *  axis, mm, from the drawing (overall length less the tip to the axis). */
  reachMm?: number;
  /** Parapet and plane mounts: overall height, mm, from the drawing. */
  heightMm?: number;
  /** Ceiling mounts: ceiling to camera, mm, one figure or a telescopic range. */
  lengthMm?: number | [number, number];
  /** Ingress protection, as the datasheet prints it (junction and power boxes). */
  ipRating?: string;
  operatingTempC?: string;
  datasheetUrl: string;
}

export const MOUNT_ACCESSORIES: readonly MountAccessory[] = [
  // Dahua "Camera Mount Series | DH-PFA156", Rev 002.000 (2022): "Pole Mount
  // Bracket"; "Aluminum alloy"; "140.0 mm × 130.4 mm × 45.0 mm"; "White";
  // "Net Weight 0.27 kg"; "Load Bearing 5.0 kg"; "Pole Diameter 80 mm–150 mm";
  // "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA156',
    kind: 'pole-mount',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 5,
    poleDiameterMm: [80, 150],
    dimensionsMm: [140, 130.4, 45],
    bracketDepthMm: 45,
    weightKg: 0.27,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/vi/products/accessories/camera-accessories/camera-mounts/pole-mount-brackets/pfa156',
  },
  // Dahua "Camera Mount Series | DH-PFA150" datasheet (2024): "Pole Mount Bracket"; "SECC; Hoop: SUS304"; "White"; "7.0 kg"; "1.09 kg"; "Φ80 mm–150 mm"; "130.4 mm × 45.0 mm × 170.0 mm"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA150',
    kind: 'pole-mount',
    material: 'SECC; hoop: SUS304',
    colour: 'white',
    loadKg: 7,
    poleDiameterMm: [80, 150],
    dimensionsMm: [130.4, 45, 170],
    bracketDepthMm: 45,
    weightKg: 1.09,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/pole-mount-brackets/pfa150',
  },
  // Dahua "Camera Mount Series | DH-PFA150-SG" datasheet (2024): "Pole Mount Bracket"; "SECC; Hoop: SUS304"; "Silver Gray"; "7.0 kg"; "1.09 kg"; "Φ80 mm–150 mm"; "130.4 mm × 45.0 mm × 170.0 mm"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA150-SG',
    kind: 'pole-mount',
    material: 'SECC; hoop: SUS304',
    colour: 'silver grey',
    loadKg: 7,
    poleDiameterMm: [80, 150],
    dimensionsMm: [130.4, 45, 170],
    bracketDepthMm: 45,
    weightKg: 1.09,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/pole-mount-brackets/pfa150-sg',
  },
  // Dahua "Camera Mount Series | DH-PFA152-E" datasheet (2025): "Pole Mount Bracket"; "Aluminum alloy; Hoop: SUS304"; "White"; "3.0 kg"; "0.26 kg"; "Φ80 mm–150 mm"; "125.6 mm × 114.0 mm × 20.0 mm"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA152-E',
    kind: 'pole-mount',
    material: 'aluminium alloy; hoop: SUS304',
    colour: 'white',
    loadKg: 3,
    poleDiameterMm: [80, 150],
    dimensionsMm: [125.6, 114, 20],
    bracketDepthMm: 20,
    weightKg: 0.26,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/pole-mount-brackets/pfa152-e',
  },
  // Dahua "Camera Mount Series | DH-PFA157" datasheet (2022): "Pole Mount Bracket"; "Aluminum alloy"; "White"; "5.0 kg"; "0.27 kg"; "80 mm–150 mm"; "140.0 mm × 130.4 mm × 45.0 mm"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA157',
    kind: 'pole-mount',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 5,
    poleDiameterMm: [80, 150],
    dimensionsMm: [140, 130.4, 45],
    bracketDepthMm: 45,
    weightKg: 0.27,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/pole-mount-brackets/pfa157',
  },
  // Dahua "Camera Mount Series | DH-PFA5510" datasheet (2024): "Pole Mount Bracket"; "SECC, SUS304"; "White"; "12.0 kg"; "1.17 kg"; "Φ60 mm–270 mm"; "130.4 mm × 170.0 mm × 45.0 mm"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA5510',
    kind: 'pole-mount',
    material: 'SECC, SUS304',
    colour: 'white',
    loadKg: 12,
    poleDiameterMm: [60, 270],
    dimensionsMm: [130.4, 170, 45],
    bracketDepthMm: 45,
    weightKg: 1.17,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/pole-mount-brackets/pfa5510',
  },
  // Dahua "Camera Mount Series | DH-PFA151" datasheet (2025): "Corner Mount Bracket"; "SECC"; "White"; "10.0 kg"; "1.70 kg"; "243.1 mm × 170.0 mm × 138.0 mm"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA151',
    kind: 'corner-mount',
    material: 'SECC',
    colour: 'white',
    loadKg: 10,
    dimensionsMm: [243.1, 170, 138],
    weightKg: 1.7,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/other-mount-brackets/pfa151',
  },
  // Dahua "Camera Mount Series | DH-PFB2203W" datasheet (2025): "Wall Mount Bracket"; "SGCC; Aluminum alloy"; "White"; "1.0 kg"; "0.48 kg"; "162.0 mm × 129.4 mm × 80.0 mm"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFB2203W',
    kind: 'wall-mount',
    material: 'SGCC; aluminium alloy',
    colour: 'white',
    loadKg: 1,
    dimensionsMm: [162, 129.4, 80],
    reachMm: 97.3,
    weightKg: 0.48,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/wall-mount-brackets/pfb2203w',
  },
  // Dahua "Camera Mount Series | DH-PFB305W" datasheet (2025): "Wall Mount Bracket"; "Aluminum alloy"; "White"; "5.0 kg"; "0.64 kg"; "202.5 mm × 115.0 mm × 160.0 mm"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFB305W',
    kind: 'wall-mount',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 5,
    dimensionsMm: [202.5, 115, 160],
    reachMm: 155.8,
    weightKg: 0.64,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/wall-mount-brackets/pfb305w',
  },
  // Dahua "Camera Mount Series | DH-PFB303W" datasheet (2025): "Wall Mount Bracket"; "Aluminum alloy"; "White"; "7.0 kg"; "0.67 kg"; "257.2 mm × 115.0 mm × 160.0 mm"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFB303W',
    kind: 'wall-mount',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 7,
    dimensionsMm: [257.2, 115, 160],
    reachMm: 228,
    weightKg: 0.67,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/wall-mount-brackets/pfb303w',
  },
  // Dahua "Camera Mount Series | DH-PFB310W" datasheet (2025): "Wall Mount Bracket"; "Aluminum alloy"; "White"; "8.0 kg"; "1.92 kg"; "363.0 mm × 135.0 mm × 174.0 mm"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFB310W',
    kind: 'wall-mount',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 8,
    dimensionsMm: [363, 135, 174],
    reachMm: 308.8,
    weightKg: 1.92,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/wall-mount-brackets/pfb310w',
  },
  // Dahua "Camera Mount Series | DH-PFB306W" datasheet (2025): "Wall Mount Bracket"; "Aluminum alloy"; "White"; "9.0 kg"; "0.96 kg"; "272.5 mm × 115.0 mm × 159.9 mm"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFB306W',
    kind: 'wall-mount',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 9,
    dimensionsMm: [272.5, 115, 159.9],
    reachMm: 227.9,
    weightKg: 0.96,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/wall-mount-brackets/pfb306w',
  },
  // Dahua "Camera Mount Series | DH-PFB2211C" datasheet (2023): "Ceiling Mount Bracket"; "SECC; aluminum alloy"; "White"; "1.0 kg"; "0.60 kg"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFB2211C',
    kind: 'ceiling-mount',
    material: 'SECC; aluminium alloy',
    colour: 'white',
    loadKg: 1,
    lengthMm: [475, 800],
    weightKg: 0.6,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/ceiling-mount-brackets/pfb2211c',
  },
  // Dahua "Camera Mount Series | DH-PFB2212C" datasheet (2023): "Ceiling Mount Bracket"; "SECC; aluminum alloy"; "White"; "1.0 kg"; "0.75 kg"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFB2212C',
    kind: 'ceiling-mount',
    material: 'SECC; aluminium alloy',
    colour: 'white',
    loadKg: 1,
    lengthMm: [815, 1500],
    weightKg: 0.75,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/ceiling-mount-brackets/pfb2212c',
  },
  // Dahua "Camera Mount Series | DH-PFB2214C" datasheet (2025): "Ceiling Mount Bracket"; "SPCC; Aluminum alloy"; "White"; "2.0 kg"; "0.91 kg"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFB2214C',
    kind: 'ceiling-mount',
    material: 'SPCC; aluminium alloy',
    colour: 'white',
    loadKg: 2,
    lengthMm: [600, 940],
    weightKg: 0.91,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/ceiling-mount-brackets/pfb2214c',
  },
  // Dahua "Camera Mount Series | DH-PFB2215C" datasheet (2025): "Ceiling Mount Bracket"; "SPCC; Aluminum alloy"; "White"; "2.0 kg"; "1.23 kg"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFB2215C',
    kind: 'ceiling-mount',
    material: 'SPCC; aluminium alloy',
    colour: 'white',
    loadKg: 2,
    lengthMm: [1030, 1800],
    weightKg: 1.23,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/ceiling-mount-brackets/pfb2215c',
  },
  // Dahua "Camera Mount Series | DH-PFB220C" datasheet (2025): "Ceiling Mount Bracket"; "Aluminum alloy"; "White"; "2.0 kg"; "0.92 kg"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFB220C',
    kind: 'ceiling-mount',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 2,
    lengthMm: 767,
    weightKg: 0.92,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/ceiling-mount-brackets/pfb220c',
  },
  // Dahua "Camera Mount Series | DH-PFA117" datasheet (2025): "Ceiling Mount Bracket"; "Aluminum alloy"; "White"; "2.0 kg"; "0.68 kg"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA117',
    kind: 'ceiling-mount',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 2,
    lengthMm: 780,
    weightKg: 0.68,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/ceiling-mount-brackets/pfa117',
  },
  // Dahua "Camera Mount Series | DH-PFB300C" datasheet (2025): "Ceiling Mount Bracket"; "Aluminum alloy, PC"; "White"; "7.0 kg"; "0.77 kg"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFB300C',
    kind: 'ceiling-mount',
    material: 'aluminium alloy, PC',
    colour: 'white',
    loadKg: 7,
    lengthMm: 235.5,
    weightKg: 0.77,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/ceiling-mount-brackets/pfb300c',
  },
  // Dahua "Camera Mount Series | DH-PFA112" datasheet (2025): "Ceiling Mount Bracket"; "Aluminum alloy"; "White"; "7.0 kg"; "0.47 kg"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA112',
    kind: 'ceiling-mount',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 7,
    lengthMm: 220,
    weightKg: 0.47,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/ceiling-mount-brackets/pfa112',
  },
  // Dahua "Camera Mount Series | DH-PFA113" datasheet (2025): "Ceiling Mount Bracket"; "Aluminum alloy"; "White"; "7.0 kg"; "1.03 kg"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA113',
    kind: 'ceiling-mount',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 7,
    lengthMm: 420,
    weightKg: 1.03,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/ceiling-mount-brackets/pfa113',
  },
  // Dahua "Camera Mount Series | DH-PFB301C" datasheet (2021): "Ceiling Mount Bracket"; "Aluminum alloy"; "White"; "10.0 kg"; "0.21 kg"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFB301C',
    kind: 'ceiling-mount',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 10,
    lengthMm: 35.5,
    weightKg: 0.21,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/ceiling-mount-brackets/pfb301c',
  },
  // Dahua "Camera Mount Series | DH-PFB303S" datasheet (2025): "Parapet Mount Bracket"; "Aluminum alloy, SUS 430"; "White"; "7.0 kg"; "4.3 kg"; "878.5 mm × 824.0 mm × 180.0 mm"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFB303S',
    kind: 'parapet-mount',
    material: 'aluminium alloy, SUS 430',
    colour: 'white',
    loadKg: 7,
    dimensionsMm: [878.5, 824, 180],
    reachMm: 824,
    heightMm: 878.5,
    weightKg: 4.3,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/other-mount-brackets/pfb303s',
  },
  // Dahua "Camera Mount Series | DH-PFB301S-E" datasheet (2025): "Plane Mount Bracket"; "Aluminum alloy"; "White"; "10.0 kg"; "1.68 kg"; "343.6 mm × 121.9 mm × 700.0 mm"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFB301S-E',
    kind: 'plane-mount',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 10,
    dimensionsMm: [343.6, 121.9, 700],
    reachMm: 343.6,
    heightMm: 700,
    weightKg: 1.68,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/other-mount-brackets/pfb301s-e',
  },
  // Dahua "Camera Mount Series | DH-PFA137" datasheet (2025): "Junction Box"; "Aluminum alloy"; "White"; "1.0 kg"; "0.23 kg"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA137',
    kind: 'junction-box',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 1,
    depthMm: 34.2,
    weightKg: 0.23,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/junction-boxes/pfa137',
  },
  // Dahua "Camera Mount Series | DH-PFA130-E" datasheet (2025): "Water-proof Junction Box"; "Aluminum alloy"; "White"; "3.0 kg"; "0.34 kg"; "IP66"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA130-E',
    kind: 'junction-box',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 3,
    depthMm: 41,
    weightKg: 0.34,
    ipRating: 'IP66',
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/junction-boxes/pfa130-e',
  },
  // Dahua "Camera Mount Series | DH-PFA3300R" datasheet (2023): "Water-proof Junction Box"; "Aluminum alloy"; "White"; "3 kg"; "0.32 kg"; "IP66"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA3300R',
    kind: 'junction-box',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 3,
    depthMm: 48.2,
    weightKg: 0.32,
    ipRating: 'IP66',
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/junction-boxes/pfa3300r',
  },
  // Dahua "Camera Mount Series | DH-PFA121" datasheet (2025): "Water-proof Junction Box"; "Aluminum alloy"; "White"; "3.0 kg"; "0.52 kg"; "134.0 mm × 133.9 mm × 56.0 mm"; "IP66"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA121',
    kind: 'junction-box',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 3,
    dimensionsMm: [134, 133.9, 56],
    depthMm: 56,
    weightKg: 0.52,
    ipRating: 'IP66',
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/junction-boxes/pfa121',
  },
  // Dahua "Camera Mount Series | DH-PFA123" datasheet (2025): "Water-proof Junction Box"; "Aluminum alloy"; "White"; "3.0 kg"; "0.54 kg"; "134.0 mm × 133.5 mm × 52.0 mm"; "IP66"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA123',
    kind: 'junction-box',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 3,
    dimensionsMm: [134, 133.5, 52],
    depthMm: 52,
    weightKg: 0.54,
    ipRating: 'IP66',
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/junction-boxes/pfa123',
  },
  // Dahua "Camera Mount Series | DH-PFA6330X" datasheet (2024): "Water-proof Junction Box"; "Aluminum alloy"; "White"; "3.0 kg"; "0.66 kg"; "173.0 mm × 133.9 mm × 143.8 mm"; "IP66"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA6330X',
    kind: 'junction-box',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 3,
    dimensionsMm: [173, 133.9, 143.8],
    depthMm: 143.8,
    weightKg: 0.66,
    ipRating: 'IP66',
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/junction-boxes/pfa6330x',
  },
  // Dahua "Camera Mount Series | DH-PFA120" datasheet (2025): "Junction Box"; "Aluminum alloy"; "White"; "12.0 kg"; "0.53 kg"; "115.0 mm × 160.0 mm × 38.8 mm"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA120',
    kind: 'junction-box',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 12,
    dimensionsMm: [115, 160, 38.8],
    depthMm: 38.8,
    weightKg: 0.53,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/junction-boxes/pfa120',
  },
  // Dahua "Camera Mount Series | DH-PFA126" datasheet (2026): "Water-proof Junction Box"; "Aluminum"; "White"; "18 kg"; "1.56 kg"; "228.4 mm × 160.4 mm × 84.7 mm"; "IP66"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA126',
    kind: 'junction-box',
    material: 'aluminium',
    colour: 'white',
    loadKg: 18,
    dimensionsMm: [228.4, 160.4, 84.7],
    depthMm: 84.7,
    weightKg: 1.56,
    ipRating: 'IP66',
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/junction-boxes/pfa126',
  },
  // Dahua "Camera Mount Series | DH-PFA106" datasheet (2025): "Mount Adapter"; "Aluminum alloy"; "White"; "1.0 kg"; "0.29 kg"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA106',
    kind: 'adapter',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 1,
    depthMm: 83.5,
    weightKg: 0.29,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/mount-adapters/pfa106',
  },
  // Dahua "Camera Mount Series | DH-PFA109" datasheet (2020): "Mount Adapter"; "Aluminum alloy"; "White"; "1.0 kg"; "0.3 kg"; "134.1 mm × 134.1 mm × 83.5 mm"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA109',
    kind: 'adapter',
    material: 'aluminium alloy',
    colour: 'white',
    loadKg: 1,
    dimensionsMm: [134.1, 134.1, 83.5],
    depthMm: 83.5,
    weightKg: 0.3,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/mount-adapters/pfa109',
  },
  // Dahua "Camera Mount Series | DH-PFA6400S" datasheet (2023): "Water-proof Power Box"; "Aluminum alloy & SECC"; "White"; "8.0 kg"; "3.38 kg"; "297.2 mm × 248.0 mm × 130.7 mm"; "IP66"; "–40 °C to +60 °C".
  {
    manufacturer: 'Dahua',
    model: 'PFA6400S',
    kind: 'power-box',
    material: 'aluminium alloy and SECC',
    colour: 'white',
    loadKg: 8,
    dimensionsMm: [297.2, 248, 130.7],
    depthMm: 130.7,
    weightKg: 3.38,
    ipRating: 'IP66',
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://www.dahuasecurity.com/products/accessories/camera-accessories/camera-mounts/power-boxes/pfa6400s',
  },
  // Ajax "MountCam B2" specifications (ajax.systems/products/specs/mountcam-b2):
  // "242 mm wall mount for a wired security IP camera. Features an adapter for
  // a camera with a motorized varifocal lens."; "100 × 136 × 242 mm"; "960 g";
  // "from –40 °C to +60 °C"; strap slots "to secure the mount to a pole";
  // compatible with the BulletCam, BulletCam HL, Ajax Superior BulletCam HLVF,
  // DomeCam Mini (HL), DomeCam HLVF, Ajax Superior DomeCam HLVF and the
  // TurretCam family; "BulletCam HLVF is not compatible with MountCam mounts."
  // No load figure is published. Reach: the 242 mm from the wall to the
  // camera's seat at its tip.
  {
    manufacturer: 'Ajax',
    model: 'MountCam B2',
    kind: 'wall-mount',
    reachMm: 242,
    dimensionsMm: [100, 136, 242],
    weightKg: 0.96,
    operatingTempC: '-40 to 60',
    datasheetUrl: 'https://ajax.systems/products/specs/mountcam-b2/',
  },
  ...IPRO_MOUNT_ACCESSORIES,
  ...MOUNTS_FROM_CAMERA_ROWS,
  ...HIKVISION_MOUNT_ACCESSORIES,
  ...UNIVIEW_MOUNT_ACCESSORIES,
];

/** The key figures of a bracket in units only, so the picker shows them in any language. */
export function mountSpecLine(m: MountAccessory): string {
  const parts: string[] = [];
  if (m.poleDiameterMm) parts.push(`Ø ${m.poleDiameterMm[0]}–${m.poleDiameterMm[1]} mm`);
  if (m.loadKg != null) parts.push(`${m.loadKg} kg`);
  if (m.ipRating) parts.push(m.ipRating);
  return parts.join(' · ');
}
